var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthenticated, useRequireAccess } from "../../auth/index.js";
import { useRedirect } from "../../routing/index.js";
import { useNotify } from "../../notification/index.js";
import { useGetOne, useUpdate, } from "../../dataProvider/index.js";
import { useTranslate } from "../../i18n/index.js";
import { useResourceContext, useGetResourceLabel, useGetRecordRepresentation, } from "../../core/index.js";
import { useMutationMiddlewares, } from "../saveContext/index.js";
/**
 * Prepare data for the Edit view.
 *
 * useEditController does a few things:
 * - it grabs the id from the URL and the resource name from the ResourceContext,
 * - it fetches the record via useGetOne,
 * - it prepares the page title.
 *
 * @param {Object} props The props passed to the Edit component.
 *
 * @return {Object} controllerProps Fetched data and callbacks for the Edit view
 *
 * @example
 *
 * import { useEditController } from 'react-admin';
 * import EditView from './EditView';
 *
 * const MyEdit = () => {
 *     const controllerProps = useEditController({ resource: 'posts', id: 123 });
 *     return <EditView {...controllerProps} {...props} />;
 * }
 */
export var useEditController = function (props) {
    if (props === void 0) { props = {}; }
    var _a = props.disableAuthentication, disableAuthentication = _a === void 0 ? false : _a, propsId = props.id, _b = props.mutationMode, mutationMode = _b === void 0 ? 'undoable' : _b, _c = props.mutationOptions, mutationOptions = _c === void 0 ? {} : _c, _d = props.queryOptions, queryOptions = _d === void 0 ? {} : _d, _e = props.redirect, redirectTo = _e === void 0 ? DefaultRedirect : _e, _f = props.redirectOnError, redirectOnError = _f === void 0 ? DefaultRedirectOnError : _f, transform = props.transform;
    var resource = useResourceContext(props);
    if (!resource) {
        throw new Error('useEditController requires a non-empty resource prop or context');
    }
    var isPendingAuthenticated = useAuthenticated({
        enabled: !disableAuthentication,
    }).isPending;
    var isPendingCanAccess = useRequireAccess({
        action: 'edit',
        resource: resource,
        enabled: !disableAuthentication && !isPendingAuthenticated,
    }).isPending;
    var getRecordRepresentation = useGetRecordRepresentation(resource);
    var translate = useTranslate();
    var notify = useNotify();
    var redirect = useRedirect();
    var routeId = useParams().id;
    if (!routeId && !propsId) {
        throw new Error('useEditController requires an id prop or a route with an /:id? parameter.');
    }
    var id = propsId !== null && propsId !== void 0 ? propsId : routeId;
    var queryMeta = queryOptions.meta, otherQueryOptions = __rest(queryOptions, ["meta"]);
    var mutationMeta = mutationOptions.meta, onSuccess = mutationOptions.onSuccess, onError = mutationOptions.onError, otherMutationOptions = __rest(mutationOptions, ["meta", "onSuccess", "onError"]);
    if ((queryMeta || mutationMeta) &&
        JSON.stringify(queryMeta) !== JSON.stringify(mutationMeta) &&
        redirectTo === false) {
        console.warn('When not redirecting after editing, query meta and mutation meta should be the same, or you will have data update issues.');
    }
    var _g = useMutationMiddlewares(), registerMutationMiddleware = _g.registerMutationMiddleware, getMutateWithMiddlewares = _g.getMutateWithMiddlewares, unregisterMutationMiddleware = _g.unregisterMutationMiddleware;
    var _h = useGetOne(resource, { id: id, meta: queryMeta }, __assign({ enabled: (!isPendingAuthenticated && !isPendingCanAccess) ||
            disableAuthentication, onError: function () {
            notify('ra.notification.item_doesnt_exist', {
                type: 'error',
            });
            redirect(redirectOnError, resource, id);
        }, refetchOnReconnect: false, refetchOnWindowFocus: false, retry: false }, otherQueryOptions)), record = _h.data, error = _h.error, isLoading = _h.isLoading, isFetching = _h.isFetching, isPaused = _h.isPaused, isPending = _h.isPending, isPlaceholderData = _h.isPlaceholderData, refetch = _h.refetch;
    // eslint-disable-next-line eqeqeq
    if (record && record.id && record.id != id) {
        throw new Error("useEditController: Fetched record's id attribute (".concat(record.id, ") must match the requested 'id' (").concat(id, ")"));
    }
    var getResourceLabel = useGetResourceLabel();
    var recordRepresentation = getRecordRepresentation(record);
    var defaultTitle = translate("resources.".concat(resource, ".page.edit"), {
        id: id,
        record: record,
        recordRepresentation: typeof recordRepresentation === 'string'
            ? recordRepresentation
            : '',
        _: translate('ra.page.edit', {
            name: getResourceLabel(resource, 1),
            id: id,
            record: record,
            recordRepresentation: typeof recordRepresentation === 'string'
                ? recordRepresentation
                : '',
        }),
    });
    var recordCached = { id: id, previousData: record };
    var _j = useUpdate(resource, recordCached, __assign(__assign({ onSuccess: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return __awaiter(void 0, void 0, void 0, function () {
                var data;
                return __generator(this, function (_a) {
                    if (onSuccess) {
                        return [2 /*return*/, onSuccess.apply(void 0, args)];
                    }
                    data = args[0];
                    notify("resources.".concat(resource, ".notifications.updated"), {
                        type: 'info',
                        messageArgs: {
                            smart_count: 1,
                            _: translate('ra.notification.updated', {
                                smart_count: 1,
                            }),
                        },
                        undoable: mutationMode === 'undoable',
                    });
                    redirect(redirectTo, resource, data.id, data);
                    return [2 /*return*/];
                });
            });
        }, onError: function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            if (onError) {
                return onError.apply(void 0, args);
            }
            var error = args[0];
            // Don't trigger a notification if this is a validation error
            // (notification will be handled by the useNotifyIsFormInvalid hook)
            var validationErrors = (_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.errors;
            var hasValidationErrors = !!validationErrors &&
                Object.keys(validationErrors).length > 0;
            if (!hasValidationErrors || mutationMode !== 'pessimistic') {
                notify(typeof error === 'string'
                    ? error
                    : error.message ||
                        'ra.notification.http_error', {
                    type: 'error',
                    messageArgs: {
                        _: typeof error === 'string'
                            ? error
                            : error instanceof Error ||
                                (typeof error === 'object' &&
                                    error !== null &&
                                    error.hasOwnProperty('message'))
                                ? // @ts-ignore
                                    error.message
                                : undefined,
                    },
                });
            }
        } }, otherMutationOptions), { mutationMode: mutationMode, returnPromise: mutationMode === 'pessimistic', getMutateWithMiddlewares: getMutateWithMiddlewares })), update = _j[0], saving = _j[1].isPending;
    var save = useCallback(function (data, _a) {
        var _b = _a === void 0 ? {} : _a, onSuccessFromSave = _b.onSuccess, onErrorFromSave = _b.onError, transformFromSave = _b.transform, metaFromSave = _b.meta;
        return Promise.resolve(transformFromSave
            ? transformFromSave(data, {
                previousData: recordCached.previousData,
            })
            : transform
                ? transform(data, {
                    previousData: recordCached.previousData,
                })
                : data).then(function (data) { return __awaiter(void 0, void 0, void 0, function () {
            var error_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, update(resource, {
                                id: id,
                                data: data,
                                meta: metaFromSave !== null && metaFromSave !== void 0 ? metaFromSave : mutationMeta,
                                previousData: record,
                            }, {
                                onError: onErrorFromSave,
                                onSuccess: onSuccessFromSave,
                            })];
                    case 1:
                        _b.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _b.sent();
                        if (((_a = error_1.body) === null || _a === void 0 ? void 0 : _a.errors) != null) {
                            return [2 /*return*/, error_1.body.errors];
                        }
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); });
    }, [
        id,
        mutationMeta,
        record,
        resource,
        transform,
        update,
        recordCached.previousData,
    ]);
    return {
        defaultTitle: defaultTitle,
        error: error,
        isFetching: isFetching,
        isLoading: isLoading,
        isPaused: isPaused,
        isPending: isPending,
        isPlaceholderData: isPlaceholderData,
        mutationMode: mutationMode,
        record: record,
        redirect: redirectTo,
        redirectOnError: redirectOnError,
        refetch: refetch,
        registerMutationMiddleware: registerMutationMiddleware,
        resource: resource,
        save: save,
        saving: saving,
        unregisterMutationMiddleware: unregisterMutationMiddleware,
    };
};
var DefaultRedirect = 'list';
var DefaultRedirectOnError = 'list';
//# sourceMappingURL=useEditController.js.map